import { CheckCircle, Shield, Sparkles, ChevronDown } from "lucide-react";
import aceleradorDiamante from "@/assets/acelerador-diamante.png";
import bonusDirectorio from "@/assets/bonus-directorio-brokers.png";
import bonusPiggybacking from "@/assets/bonus-piggybacking.png";
import bonusRutaDinero from "@/assets/bonus-ruta-dinero.png";
import { useEffect } from "react";
import { useHotmartIframeFix } from "@/hooks/use-hotmart-iframe-fix";
import LazyVideo from "@/components/landing/LazyVideo";

const Upsell = () => {
  useHotmartIframeFix("hotmart-sales-funnel", {
    minHeightDesktop: 260,
    maxHeightDesktop: 300,
    viewportFillDesktop: 0.28,
    minHeightMobile: 260,
    maxHeightMobile: 300,
    viewportFillMobile: 0.32,
  });

  useEffect(() => {
    // Load Hotmart checkout script
    const script = document.createElement('script');
    script.src = 'https://checkout.hotmart.com/lib/hotmart-checkout-elements.js';
    script.async = true;
    script.onload = () => {
      // Initialize Sales Funnel widget after script loads
      if ((window as any).checkoutElements) {
        (window as any).checkoutElements.init('salesFunnel').mount('#hotmart-sales-funnel');
      }
    };
    document.body.appendChild(script);
    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://checkout.hotmart.com/lib/hotmart-checkout-elements.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);
  return <main className="min-h-screen bg-gradient-to-b from-[#0a0a0f] via-[#0d1117] to-[#0a0a0f] text-white">
      {/* Top Urgency Bar */}
      <div className="bg-gradient-to-r from-teal-600 via-cyan-500 to-amber-500 py-3 px-4 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBIMHoiLz48cGF0aCBkPSJNMjAgMjBtLTEgMGExIDEgMCAxIDAgMiAwYTEgMSAwIDEgMCAtMiAwIiBmaWxsPSJyZ2JhKDI1NSwyNTUsMjU1LDAuMSkiLz48L2c+PC9zdmc+')] opacity-30"></div>
        <p className="md:text-base font-bold relative z-10 text-slate-900 text-xl">
          ⚠️ ESPERA: TU ORDEN NO ESTÁ COMPLETA. OFERTA DE UNA SOLA VEZ.
        </p>
        {/* Progress Bar */}
        <div className="max-w-xs mx-auto mt-2 relative z-10">
          <div className="h-2 bg-black/20 rounded-full overflow-hidden">
            <div className="h-full w-[80%] bg-gradient-to-r from-teal-300 to-amber-300 rounded-full animate-pulse"></div>
          </div>
          <p className="mt-1 text-slate-800 text-xl font-extrabold">80% Completado</p>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 md:py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          {/* Pre-title */}
          <p className="text-[#c0c0c0] md:text-base uppercase tracking-widest mb-4 font-medium text-xl">
            NO ESPERES 10 AÑOS PARA TENER UN HISTORIAL PERFECTO...
          </p>
          
          {/* Main Title */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
            <span className="bg-gradient-to-r from-[#4facfe] via-[#00f2fe] to-[#4facfe] bg-clip-text text-transparent drop-shadow-[0_0_30px_rgba(79,172,254,0.5)]">
              ACTIVA EL ACELERADOR
            </span>
            <br />
            <span className="bg-gradient-to-r from-white via-[#e0e0e0] to-white bg-clip-text text-transparent">
              DE DIAMANTE
            </span>
            <Sparkles className="inline-block w-8 h-8 md:w-12 md:h-12 ml-2 text-[#4facfe] animate-pulse" />
          </h1>
          
          {/* Subtitle */}
          <p className="text-lg md:text-xl text-[#a0a0a0] max-w-3xl mx-auto mb-8 leading-relaxed">
            La estrategia secreta para <strong className="text-white">"alquilar" antigüedad y límites de $50,000</strong>, 
            saltando a <strong className="text-[#4facfe]">750 puntos en menos de 30 días</strong>.
          </p>

          <ChevronDown className="w-8 h-8 mx-auto text-[#4facfe] animate-bounce" />
        </div>
      </section>

      {/* Video/Product Image Section */}
      <section className="py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Video Explicativo */}
          <div className="aspect-[9/16] max-w-sm mx-auto rounded-2xl overflow-hidden border border-[#4facfe]/30 mb-8 shadow-[0_0_60px_rgba(79,172,254,0.2)]">
            <LazyVideo 
              className="w-full h-full object-cover" 
              poster="/images/video-poster-upsell.png"
              src="/videos/video-acelerador.mp4"
            />
          </div>

          {/* 3D Product Mockup */}
          <div className="relative mx-auto max-w-sm">
            <div className="absolute inset-0 bg-gradient-to-r from-[#4facfe] to-[#00f2fe] blur-3xl opacity-30"></div>
            <img src={aceleradorDiamante} alt="El Acelerador de Diamante - Manual Digital" className="relative w-full rounded-lg shadow-[0_0_60px_rgba(79,172,254,0.4)] hover:scale-105 transition-transform duration-300" loading="lazy" decoding="async" />
          </div>
        </div>
      </section>

      {/* Sales Body - The Secret */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold mb-6">
              <span className="text-[#c0c0c0]">El Secreto de las</span>{" "}
              <span className="text-[#4facfe]">Tradelines</span>
            </h2>
            <p className="text-[#a0a0a0] text-lg leading-relaxed">
              Limpiar tu crédito <strong className="text-white">no es suficiente</strong>. Necesitas añadir 
              <strong className="text-[#4facfe]"> historia positiva</strong>. Este es el atajo que usan las 
              personas con acceso a información privilegiada — y ahora tú también puedes acceder.
            </p>
          </div>

          {/* Bullet Points */}
          <div className="space-y-6">
            <div className="flex items-start gap-4 bg-gradient-to-r from-[#1a1a2e]/80 to-transparent p-5 rounded-xl border-l-4 border-[#4facfe]">
              <CheckCircle className="w-6 h-6 text-[#00f2fe] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-white text-lg">Inyecta 5+ Años de Historia Positiva Instantáneamente</h4>
                <p className="text-[#a0a0a0] mt-1">Hereda la antigüedad de cuentas establecidas sin esperar una década.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-gradient-to-r from-[#1a1a2e]/80 to-transparent p-5 rounded-xl border-l-4 border-[#4facfe]">
              <CheckCircle className="w-6 h-6 text-[#00f2fe] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-white text-lg">Hereda Límites Altos Para "Engañar" al Algoritmo Legalmente</h4>
                <p className="text-[#a0a0a0] mt-1">Los burós ven límites de $50,000+ y tu score se dispara automáticamente.</p>
              </div>
            </div>

            <div className="flex items-start gap-4 bg-gradient-to-r from-[#1a1a2e]/80 to-transparent p-5 rounded-xl border-l-4 border-[#4facfe]">
              <CheckCircle className="w-6 h-6 text-[#00f2fe] flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-white text-lg">Acceso a Mi Lista Privada de Brokers Verificados</h4>
                <p className="text-[#a0a0a0] mt-1">Solo proveedores confiables que he probado personalmente. Sin estafas.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bonus Section */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0d1117] rounded-2xl p-8 border-2 border-dashed border-[#4facfe]/50 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#4facfe]/10 rounded-full blur-3xl"></div>
            
            <div className="text-center mb-8 relative z-10">
              <span className="inline-block bg-gradient-to-r from-teal-400 via-cyan-400 to-amber-400 text-slate-900 text-xs font-bold px-4 py-1 rounded-full mb-4">
                BONOS ELITE INCLUIDOS
              </span>
              <h3 className="text-2xl md:text-3xl font-bold text-white">
                Solo Disponibles en Esta Página
              </h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6 relative z-10">
              {/* Bonus 1 */}
              <div className="bg-[#0a0a0f]/60 rounded-xl p-6 border border-[#c0c0c0]/20 hover:border-[#4facfe]/50 transition-all text-center">
                <img src={bonusDirectorio} alt="Directorio Privado de Brokers" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" loading="lazy" decoding="async" />
                <h4 className="font-bold text-white text-lg mb-2">Directorio de Proveedores Confiables</h4>
                <p className="text-[#a0a0a0] text-sm">Lista curada de brokers verificados para evitar estafas y obtener las mejores tradelines.</p>
              </div>

              {/* Bonus 2 */}
              <div className="bg-[#0a0a0f]/60 rounded-xl p-6 border border-[#c0c0c0]/20 hover:border-[#4facfe]/50 transition-all text-center">
                <img src={bonusPiggybacking} alt="Estrategia Piggybacking" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" loading="lazy" decoding="async" />
                <h4 className="font-bold text-white text-lg mb-2">Estrategia de Piggybacking</h4>
                <p className="text-[#a0a0a0] text-sm">El multiplicador de score que te permite "montarte" en el historial de otros legalmente.</p>
              </div>

              {/* Bonus 3 */}
              <div className="bg-[#0a0a0f]/60 rounded-xl p-6 border border-[#c0c0c0]/20 hover:border-[#4facfe]/50 transition-all text-center">
                <img src={bonusRutaDinero} alt="La Ruta del Dinero" className="w-32 h-32 mx-auto object-contain mb-4 rounded-lg" loading="lazy" decoding="async" />
                <h4 className="font-bold text-white text-lg mb-2">La Ruta del Dinero</h4>
                <p className="text-[#a0a0a0] text-sm">El Mapa de Aplicación Secreta para conseguir aprobaciones más rápido.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="mb-8">
            <p className="text-[#a0a0a0] text-lg mb-2">Precio Regular:</p>
            <p className="text-4xl font-bold text-[#c0c0c0] line-through opacity-60">$67</p>
          </div>
          
          <div className="mb-8">
            <p className="text-[#4facfe] text-lg font-medium mb-2">Precio Especial de Hoy:</p>
            <p className="text-6xl md:text-7xl font-black bg-gradient-to-r from-[#4facfe] via-[#00f2fe] to-[#4facfe] bg-clip-text text-transparent">
              $14.97
            </p>
            <p className="text-[#a0a0a0] text-sm mt-2">Pago único • Acceso inmediato</p>
          </div>

          {/* CTA ya está abajo, widget movido al footer */}

          {/* Botón de respaldo por si el widget no carga */}
          <a
            href="/downsell"
            className="inline-flex flex-col items-center justify-center w-full max-w-md mx-auto bg-gradient-to-r from-teal-500 via-cyan-400 to-amber-500 text-slate-900 font-black text-base md:text-xl py-5 px-6 rounded-xl shadow-[0_0_40px_rgba(20,184,166,0.5)] hover:shadow-[0_0_60px_rgba(20,184,166,0.7)] transition-all duration-300 animate-pulse hover:scale-105 text-center"
          >
            <span className="text-lg sm:text-2xl md:text-3xl mb-1 leading-tight">SÍ, QUIERO ACELERAR MI CRÉDITO</span>
            <span className="text-xs sm:text-sm font-medium opacity-90">Agregar por solo $14.97</span>
          </a>

          {/* NO Link */}
          <div className="mt-8">
            <a href="/downsell" className="text-sm text-[#606060] hover:text-[#808080] underline transition-colors">
              No gracias, prefiero construir mi crédito lentamente durante los próximos años.
            </a>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-12 px-4">
        <div className="max-w-md mx-auto text-center">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0d1117] rounded-2xl p-8 border border-[#c0c0c0]/30">
            <div className="w-20 h-20 mx-auto bg-gradient-to-br from-[#c0c0c0]/20 to-[#808080]/20 rounded-full flex items-center justify-center mb-4 border-2 border-[#c0c0c0]/50">
              <Shield className="w-10 h-10 text-orange-700" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Satisfacción Garantizada</h3>
            <p className="text-[#a0a0a0] text-sm">
              30 días de garantía de devolución. Si no ves resultados, te devolvemos tu dinero. Sin preguntas.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-[#1a1a2e]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-[#606060] mb-6">
            © {new Date().getFullYear()} Universo Digital Academy. Todos los derechos reservados.
          </p>
        </div>

        {/* Hotmart Sales Funnel Widget (full-bleed) */}
        <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen">
          <div id="hotmart-sales-funnel" className="w-full" />
        </div>
      </footer>
    </main>;
};
export default Upsell;